/* Sfeervuur performance monitor: watches real frame times and moves the tier chosen in
   snippets/sf-perf.liquid. Down fast when frames drop; up only to win back a tier lost earlier
   (never above the device estimate), after a long smooth stretch.
   Only runs while something moves (scroll, pointer, or an effect calling SF.perf.ping()).
   ?sf-perf=1 shows a small diagnostics panel with manual tier buttons. */
(() => {
  'use strict';

  const perf = window.SF && window.SF.perf;
  if (!perf || perf.monitor) return;

  const prof = perf.prof;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const S_KEY = 'sf-perf-s';
  const WIN = 45; // frames per judgement
  const UP_AFTER = 30000; // ms of smooth, real animation before winning back one tier
  const SLOW = 18.5; // ms: a frame this long is visible stutter on any screen (below ~54 fps)

  const sess = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(S_KEY)) || {};
    } catch (e) {
      return {};
    }
  })();
  sess.down = sess.down || 0;
  sess.up = sess.up || 0;
  const saveSess = () => {
    try {
      sessionStorage.setItem(S_KEY, JSON.stringify(sess));
    } catch (e) {
      /* private mode: the tier still adapts within the page */
    }
  };

  const mon = {
    raf: 0,
    last: 0,
    until: 0,
    heavyUntil: 0,
    ready: false,
    vs: 0, // display refresh interval (ms), the shortest steady frame seen
    win: [],
    heavy: 0,
    bad: 0,
    cool: 0,
    clean: 0, // ms of smooth heavy animation since the last tier change
    cleanDrops: 0,
    cleanFrames: 0,
    fps: 0,
    drop: 0,
    log: [],
  };
  perf.monitor = mon;

  /* ---------- sampling ---------- */

  function step(t) {
    mon.raf = 0;
    if (document.hidden) {
      mon.last = 0;
      return;
    }
    if (mon.last) {
      const d = t - mon.last;
      // gaps over 250 ms are pauses (tab switch, dialogs), not jank
      if (d > 0 && d < 250) record(d, t < mon.heavyUntil);
    }
    mon.last = t;
    if (t < mon.until) mon.raf = window.requestAnimationFrame(step);
    else mon.last = 0;
  }

  // keep the monitor running for `ms`; heavy = a real effect is animating (counts towards upgrades)
  function ping(ms = 1500, heavy = false) {
    const t = performance.now();
    if (t + ms > mon.until) mon.until = t + ms;
    if (heavy && t + ms > mon.heavyUntil) mon.heavyUntil = t + ms;
    if (!mon.raf && !document.hidden) {
      mon.last = 0;
      mon.raf = window.requestAnimationFrame(step);
    }
  }
  perf.ping = ping;

  function record(d, heavy) {
    if (!mon.ready) return;
    if (mon.cool > 0) {
      mon.cool--;
      return;
    }
    mon.win.push(d);
    if (heavy) mon.heavy++;
    if (mon.win.length >= WIN) judge();
  }

  /* ---------- decisions ---------- */

  function judge() {
    const w = mon.win;
    const heavy = mon.heavy > w.length / 2;
    mon.win = [];
    mon.heavy = 0;

    const sorted = [...w].sort((a, b) => a - b);
    const p20 = sorted[Math.floor(w.length * 0.2)];
    mon.vs = clamp(mon.vs ? Math.min(mon.vs, p20) : p20, 6.5, 34);
    // a frame counts as dropped past 1.5 refresh intervals, and always past SLOW: on a 144 Hz screen
    // an uneven 40-60 fps is exactly the stutter people notice
    const lim = Math.max(mon.vs * 1.5, SLOW);
    let drops = 0;
    let sum = 0;
    for (const d of w) {
      sum += d;
      if (d > lim) drops++;
    }
    const ratio = drops / w.length;
    const mean = sum / w.length;
    mon.fps = 1000 / mean;
    mon.drop = ratio;
    panel.update();

    if (perf.forced) return;

    // below ~40 fps on average (or many frames dropped): two tiers down at once, three below ~25 fps
    if (ratio > 0.4 || mean > Math.max(mon.vs * 2, 25)) {
      down(mean > Math.max(mon.vs * 3, 40) ? 3 : 2, `${Math.round(mon.fps)} fps`);
    } else if (ratio > 0.15) {
      // lighter stutter: one tier after two such windows in a row (about a second of motion)
      if (++mon.bad >= 2) down(1, `${Math.round(ratio * 100)}% frames dropped`);
    } else {
      mon.bad = 0;
      if (heavy) {
        mon.clean += sum;
        mon.cleanDrops += drops;
        mon.cleanFrames += w.length;
        maybeUp();
      }
    }
  }

  function settle() {
    mon.bad = 0;
    mon.cool = 60; // let the new settings take effect before judging again
    mon.clean = 0;
    mon.cleanDrops = 0;
    mon.cleanFrames = 0;
  }

  function down(n, why) {
    const from = perf.tier;
    const to = Math.max(1, from - n);
    if (to === from) return;
    // the tier we climbed to this visit is too much: do not try it again for a while
    if (sess.upAt === from) {
      prof.ceil = from - 1;
      prof.ceilAt = Date.now();
    }
    sess.down++;
    saveSess();
    settle();
    note(`${from} → ${to}: ${why}`);
    perf.set(to, why);
  }

  // Only wins back a tier lost on an earlier visit (one per visit), never above the device
  // estimate: the estimate already has a safety margin, and a tier that turns out too high
  // costs the visitor real stutter before the monitor can react.
  function maybeUp() {
    if (sess.down || sess.up >= 1) return;
    if (perf.tier >= Math.min(prof.base || 1, prof.ceil || 20, perf.cap)) return;
    if (mon.clean < UP_AFTER) return;
    if (mon.cleanDrops / mon.cleanFrames > 0.02) {
      mon.clean = 0;
      mon.cleanDrops = 0;
      mon.cleanFrames = 0;
      return;
    }
    const from = perf.tier;
    sess.up++;
    sess.upAt = from + 1;
    saveSess();
    settle();
    note(`${from} → ${from + 1}: smooth`);
    perf.set(from + 1, 'smooth');
  }

  function note(s) {
    mon.log.unshift(s);
    mon.log.length = Math.min(mon.log.length, 4);
    panel.update();
  }

  /* ---------- wiring ---------- */

  const wake = () => ping(1500);
  document.addEventListener('scroll', wake, { capture: true, passive: true });
  document.addEventListener('wheel', wake, { passive: true });
  document.addEventListener('touchmove', wake, { passive: true });
  let lastMove = 0;
  document.addEventListener(
    'pointermove',
    (e) => {
      if (e.timeStamp - lastMove < 200) return;
      lastMove = e.timeStamp;
      ping(900);
    },
    { passive: true }
  );
  document.addEventListener('visibilitychange', () => {
    mon.last = 0;
    if (!document.hidden && performance.now() < mon.until) ping(0);
  });

  // the CPU test in <head> runs while the page loads and reads low; repeat it a few times when the
  // browser is idle. A better result only raises the start tier for the next pages, and only if
  // nothing measured on this visit has lowered it.
  function rebench() {
    if (!perf.bench || (prof.runs || 0) >= 4) return;
    const b = Math.round(perf.bench());
    prof.runs = (prof.runs || 0) + 1;
    if (b > prof.bench) {
      prof.bench = b;
      const base = perf.rescore(b);
      if (base > prof.base) {
        if (!sess.down && !perf.forced && prof.tier < base && base <= (prof.ceil || 20)) prof.tier = base;
        prof.base = base;
      }
    }
    perf.save();
  }

  // page load itself (parsing, decoding images) is not something a lower tier can fix
  const arm = () =>
    window.setTimeout(() => {
      mon.ready = true;
      ping(3000);
      if (window.requestIdleCallback) window.requestIdleCallback(rebench, { timeout: 4000 });
      else window.setTimeout(rebench, 2500);
    }, 400);
  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm, { once: true });

  /* ---------- diagnostics panel (?sf-perf=1) ---------- */

  const panel = {
    el: null,
    update() {
      if (!this.el) return;
      const f = mon.fps ? `${Math.round(mon.fps)} fps · ${Math.round(mon.drop * 100)}% drops · ${mon.vs ? mon.vs.toFixed(1) : '–'} ms` : 'waiting for motion…';
      this.el.querySelector('[data-t]').textContent = `Tier ${perf.tier}/20 ${perf.forced ? '(test)' : '(auto)'}`;
      this.el.querySelector('[data-f]').textContent = f;
      this.el.querySelector('[data-l]').textContent = mon.log.join('\n');
    },
    mount() {
      let on = false;
      try {
        on = !!sessionStorage.getItem('sf-perf-debug');
      } catch (e) {
        /* ignore */
      }
      if (!on || this.el) return;
      const el = document.createElement('div');
      el.setAttribute('data-sf-perf-panel', '');
      el.style.cssText =
        'position:fixed;left:10px;bottom:10px;z-index:2147483646;min-width:220px;max-width:300px;padding:10px 12px;' +
        'font:11px/1.45 ui-monospace,Consolas,monospace;color:#f4ecdd;background:rgb(5 5 5 / .88);' +
        'border:1px solid rgb(219 166 96 / .5);white-space:pre-wrap;pointer-events:auto';
      const btn = 'margin:6px 6px 0 0;padding:2px 9px;font:inherit;color:#050505;background:#dba660;border:0;cursor:pointer';
      el.innerHTML =
        `<b data-t style="color:#f0d090"></b>\n<span data-f></span>\n` +
        `start ${prof.base} · score ${prof.score} · ceiling ${prof.ceil}\n${prof.gpu || 'gpu ?'}\n${prof.notes || ''}\n` +
        `<span data-l style="color:#dba660"></span>` +
        `<div><button type="button" data-d style="${btn}">−</button><button type="button" data-u style="${btn}">+</button>` +
        `<button type="button" data-a style="${btn}">auto</button></div>`;
      const force = (t) => {
        t = clamp(t, 1, 20);
        try {
          sessionStorage.setItem('sf-tier', JSON.stringify(t));
        } catch (e) {
          /* ignore */
        }
        perf.forced = true;
        perf.set(t, 'test');
        this.update();
      };
      el.querySelector('[data-d]').addEventListener('click', () => force(perf.tier - 1));
      el.querySelector('[data-u]').addEventListener('click', () => force(perf.tier + 1));
      el.querySelector('[data-a]').addEventListener('click', () => {
        try {
          sessionStorage.removeItem('sf-tier');
        } catch (e) {
          /* ignore */
        }
        window.location.reload();
      });
      document.body.appendChild(el);
      this.el = el;
      this.update();
      document.addEventListener('sf:perf', () => this.update());
    },
  };

  if (document.body) panel.mount();
  else document.addEventListener('DOMContentLoaded', () => panel.mount(), { once: true });
})();
